
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Brain, FileSpreadsheet, Target, RefreshCcw, Download } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useOpenAIService } from "@/hooks/useOpenAIService";
import { usePredictiveModel } from "@/hooks/usePredictiveModel";
import { useDataImport } from "@/hooks/useDataImport";
import { GoalCreationDialog } from "@/components/goals/GoalCreationDialog";
import { useState } from "react";

const QuickActions = () => {
  const { callAIService, isLoading: aiLoading } = useOpenAIService();
  const { generatePrediction, isGenerating } = usePredictiveModel();
  const { importFile, importFromIntegration, importing, progress } = useDataImport();
  const [refreshing, setRefreshing] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [showGoalDialog, setShowGoalDialog] = useState(false);

  const handleSetNewGoal = () => {
    setShowGoalDialog(true);
  };

  const handleImportData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please log in to import data");
        return;
      }

      // Create file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.csv,.xlsx,.json,.xls';
      input.multiple = false;
      
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;

        await importFile(file);
      };
      
      input.click();
    } catch (error) {
      console.error("Error importing data:", error);
      toast.error("Failed to initiate data import");
    }
  };

  const handleTrainModel = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please log in to train models");
        return;
      }

      // Use the existing predictive model hook to generate a new model
      await generatePrediction({
        modelType: 'classification',
        industry: 'tech',
        dataSource: 'dashboard_data',
        target: 'performance_metric'
      });

      toast.success("Model training initiated successfully!");
    } catch (error) {
      console.error("Error training model:", error);
      toast.error("Failed to initiate model training");
    }
  };

  const handleRefreshData = async () => {
    setRefreshing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please log in to refresh data");
        return;
      }

      // Call enhanced data refresh function
      const { data, error } = await supabase.functions.invoke('enhanced-data-refresh', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        }
      });

      if (error) throw error;

      if (data?.success) {
        toast.success(`Data refreshed! Processed ${data.totalRecordsProcessed} records from ${data.insights.activeConnections} sources`);
      } else {
        toast.error("No new data found to refresh");
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Failed to refresh data");
    } finally {
      setRefreshing(false);
    }
  };

  const handleTakeSnapshot = async () => {
    setSnapshotting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please log in to take snapshots");
        return;
      }

      // Create export record for snapshot
      const { data, error } = await supabase
        .from('exports')
        .insert({
          user_id: user.id,
          resource_type: 'dashboard_snapshot',
          resource_id: crypto.randomUUID(),
          format: 'png',
          status: 'completed'
        });

      if (error) throw error;

      // Generate and download snapshot
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 1200;
      canvas.height = 800;
      
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#000000';
        ctx.font = '24px Arial';
        ctx.fillText('Dashboard Snapshot', 50, 50);
        ctx.font = '16px Arial';
        ctx.fillText(`Generated on: ${new Date().toLocaleDateString()}`, 50, 100);
      }

      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `dashboard-snapshot-${new Date().getTime()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      });

      toast.success("Dashboard snapshot captured and downloaded!");
    } catch (error) {
      console.error("Error taking snapshot:", error);
      toast.error("Failed to capture snapshot");
    } finally {
      setSnapshotting(false);
    }
  };

  const actions = [
    {
      title: "Set New Goal",
      description: "Define objectives and KPIs",
      icon: Target,
      onClick: handleSetNewGoal,
      loading: false,
    },
    {
      title: "Import Data",
      description: "Connect spreadsheet or upload file",
      icon: FileSpreadsheet,
      onClick: handleImportData,
      loading: importing,
    },
    {
      title: "Train Model",
      description: "Create new ML prediction model",
      icon: Brain,
      onClick: handleTrainModel,
      loading: isGenerating,
    },
    {
      title: "Refresh Data",
      description: "Update dashboard with latest data",
      icon: RefreshCcw,
      onClick: handleRefreshData,
      loading: refreshing || aiLoading,
    },
    {
      title: "Take Snapshot",
      description: "Capture current dashboard state",
      icon: Download,
      onClick: handleTakeSnapshot,
      loading: snapshotting,
    },
  ];

  return (
    <>
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        
        {importing && progress > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span>Importing data...</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {actions.map((action) => (
            <Button
              key={action.title}
              variant="outline"
              className="h-auto py-4 px-4 flex flex-col items-center text-center hover:bg-accent"
              onClick={action.onClick}
              disabled={action.loading}
            >
              <action.icon className={`w-6 h-6 mb-2 text-primary ${action.loading ? 'animate-spin' : ''}`} />
              <span className="font-medium text-sm">{action.title}</span>
              <span className="text-xs text-gray-500 mt-1">
                {action.loading ? 'Processing...' : action.description}
              </span>
            </Button>
          ))}
        </div>
      </Card>
      
      <GoalCreationDialog 
        open={showGoalDialog}
        onOpenChange={setShowGoalDialog}
        onGoalCreated={() => {
          setShowGoalDialog(false);
          toast.success("Goal created successfully!");
        }} 
      />
    </>
  );
};

export default QuickActions;
